/**
 * Weekly Meal Selection route
 * User selects meals for the week based on their availability
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import type { RootSearchParams } from '@/routes/__root';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { requireAuth } from '@/lib/auth/routeGuards';
import { useToast } from '@/components/ui/ToastProvider';
import { useWeek } from '@/contexts/WeekContext';
import {
  useWeeklySelections,
  useAvailableMeals,
  useBulkUpdateWeeklySelections,
  useFamilySummary,
} from '@/hooks/useWeeklySelections';
import { useEnrichedAvailability } from '@/hooks/useEnrichedAvailability';
import { WeekNavigator } from '@/components/availability/WeekNavigator';
import { MealSelectionCard } from '@/components/weekly-selections/MealSelectionCard';
import { WeeklySelectionSummary } from '@/components/weekly-selections/WeeklySelectionSummary';
import { FamilyMemberCard } from '@/components/weekly-selections/FamilyMemberCard';
import { FamilySummarySkeleton } from '@/components/weekly-selections/FamilySummarySkeleton';
import { formatDateForAPI, getWeekRangeForAPI } from '@/lib/utils/date';

export const Route = createFileRoute('/weekly-selection/')({
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as 'personal' | 'family') || 'personal',
  }),
  component: WeeklySelectionComponent,
});

function WeeklySelectionComponent() {
  // Get translations and toast
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  // Get root search params to preserve week when navigating
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;

  // Get user and tab from route
  const { user } = Route.useRouteContext();
  const { tab } = Route.useSearch();
  const userId = user.id;

  // Use global week context
  const { weekStart, weekStartDate, goToPreviousWeek, goToNextWeek, goToToday } = useWeek();

  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch user's availability for the week (enriched with default availability)
  const weekRange = getWeekRangeForAPI(weekStart);
  const { data: enrichedAvailability } = useEnrichedAvailability({
    user,
    startDate: weekRange.startDate,
    endDate: weekRange.endDate,
  });

  // Calculate required meal count from enriched availability (excluding past days)
  const requiredMealCount = useMemo(() => {
    if (!enrichedAvailability) return 0;

    // Get today's date at midnight for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = formatDateForAPI(today);

    // Only count meals for today and future days
    return enrichedAvailability.availability.filter((a) => {
      return a.isPresent && a.date >= todayString;
    }).length;
  }, [enrichedAvailability]);

  // Fetch available meals
  const {
    data: mealsData,
    isLoading: mealsLoading,
    error: mealsError,
  } = useAvailableMeals({
    userId,
    weekStartDate,
  });

  // Fetch current selections
  const { data: selectionsData, isLoading: selectionsLoading } = useWeeklySelections({
    userId,
    weekStartDate,
  });

  // Fetch family summary (only when on family tab)
  const { data: familyData, isLoading: familyLoading } = useFamilySummary(
    user.isOrganizer && tab === 'family'
      ? { weekStartDate }
      : undefined
  );

  // Local state for meal selections (meal ID -> count)
  const [localSelections, setLocalSelections] = useState<Record<number, number>>({});

  // Initialize local selections from server data
  useEffect(() => {
    if (selectionsData?.selections) {
      const counts: Record<number, number> = {};
      selectionsData.selections.forEach((selection) => {
        counts[selection.mealId] = selection.selectionCount;
      });
      setLocalSelections(counts);
    }
  }, [selectionsData]);

  // Bulk update mutation
  const bulkUpdateMutation = useBulkUpdateWeeklySelections();

  // Calculate total selected count
  const totalSelectedCount = Object.values(localSelections).reduce((sum, count) => sum + count, 0);

  // Get the meals that are selected
  const selectedMealsList = useMemo(() => {
    if (!mealsData?.meals) return [];
    return mealsData.meals.filter((meal) => (localSelections[meal.id] || 0) > 0);
  }, [mealsData, localSelections]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!selectionsData?.selections) return totalSelectedCount > 0;

    const serverCounts: Record<number, number> = {};
    selectionsData.selections.forEach((selection) => {
      serverCounts[selection.mealId] = selection.selectionCount;
    });

    // Compare local and server selections
    const allMealIds = new Set([
      ...Object.keys(localSelections).map(Number),
      ...Object.keys(serverCounts).map(Number),
    ]);

    for (const mealId of allMealIds) {
      if ((localSelections[mealId] || 0) !== (serverCounts[mealId] || 0)) {
        return true;
      }
    }

    return false;
  }, [localSelections, selectionsData, totalSelectedCount]);

  // Filter meals based on search
  const filteredMeals = useMemo(() => {
    if (!mealsData?.meals) return [];

    if (!searchQuery.trim()) return mealsData.meals;

    const query = searchQuery.toLowerCase();
    return mealsData.meals.filter(
      (meal) =>
        meal.name.toLowerCase().includes(query) ||
        meal.tags.some((tag) => tag.tag.toLowerCase().includes(query))
    );
  }, [mealsData, searchQuery]);

  // Handlers
  const handleIncrement = (mealId: number) => {
    setLocalSelections((prev) => ({
      ...prev,
      [mealId]: (prev[mealId] || 0) + 1,
    }));
  };

  const handleDecrement = (mealId: number) => {
    setLocalSelections((prev) => {
      const newCount = (prev[mealId] || 0) - 1;
      if (newCount <= 0) {
        // Remove the meal from selections if count reaches 0
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [mealId]: _removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [mealId]: newCount,
      };
    });
  };

  const handleSave = () => {
    // Convert local selections to bulk format with selectionCount
    const selections = Object.entries(localSelections)
      .filter(([, count]) => count > 0)
      .map(([mealId, count]) => ({
        mealId: Number(mealId),
        selectionCount: count,
      }));

    bulkUpdateMutation.mutate(
      {
        userId,
        weekStartDate,
        selections,
      },
      {
        onSuccess: () => {
          toast.success(t('weeklySelection.saveSuccessful'));
        },
        onError: (error) => {
          console.error('Failed to save selections:', error);
          toast.error(t('weeklySelection.saveFailed'));
        },
      }
    );
  };

  const handleCancel = () => {
    // Reset to server data
    if (selectionsData?.selections) {
      const counts: Record<number, number> = {};
      selectionsData.selections.forEach((selection) => {
        counts[selection.mealId] = selection.selectionCount;
      });
      setLocalSelections(counts);
    } else {
      setLocalSelections({});
    }
  };

  // Tab navigation handler (preserving week param)
  const handleTabChange = (newTab: 'personal' | 'family') => {
    if (hasChanges) {
      if (!confirm(t('weeklySelection.discardConfirm'))) {
        return;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ search: { ...rootSearch, tab: newTab } as any });
  };

  // Loading state
  const isLoading = mealsLoading || selectionsLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('weeklySelection.title')}</h1>
          <p className="text-sm text-gray-600">
            {t('weeklySelection.description')}
          </p>
        </div>

        {/* Tabs for organizers */}
        {user.isOrganizer && (
          <div className="mb-6 border-b border-gray-200">
            <div className="flex space-x-8">
              <button
                onClick={() => handleTabChange('personal')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  tab === 'personal'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('weeklySelection.mySelection')}
              </button>
              <button
                onClick={() => handleTabChange('family')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  tab === 'family'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('weeklySelection.familyView')}
              </button>
            </div>
          </div>
        )}

        {/* Personal Tab Content */}
        {(!user.isOrganizer || tab === 'personal') && (
          <>
            {/* Week Navigator */}
            <WeekNavigator
              currentWeekStart={weekStart}
              onPreviousWeek={goToPreviousWeek}
              onNextWeek={goToNextWeek}
              onToday={goToToday}
            />

        {/* Error state */}
        {mealsError && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('weeklySelection.errorLoadingMeals')}</h3>
                <div className="mt-2 text-sm text-red-700">
                  {mealsError instanceof Error
                    ? mealsError.message
                    : t('weeklySelection.failedToLoadMeals')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Availability info banners */}
        {!isLoading && requiredMealCount === 0 && (
          <div className="rounded-md bg-yellow-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t('weeklySelection.noMealsForThisWeek')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  {t('weeklySelection.noAvailabilityWarning')}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && requiredMealCount === 14 && (
          <div className="rounded-md bg-blue-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('weeklySelection.allMealsSelected')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  {t('weeklySelection.allMealsSelectedDescription', { count: requiredMealCount })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Meals grid */}
          <div className="lg:col-span-2">
            {/* Search bar and shortcuts */}
            <div className="mb-6">
              <label htmlFor="meal-search" className="sr-only">
                {t('weeklySelection.searchPlaceholder')}
              </label>
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  id="meal-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('weeklySelection.searchPlaceholder')}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              {/* Filter shortcuts */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSearchQuery('sain')}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  title={t('weeklySelection.filterHealthyTooltip')}
                >
                  <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t('weeklySelection.filterHealthy')}
                </button>
                {searchQuery.trim() && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('weeklySelection.clearFilters')}
                  </button>
                )}
              </div>
            </div>

            {/* Loading state */}
            {isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-md p-4 animate-pulse"
                  >
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-10 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredMeals.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('weeklySelection.noMealsFound')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery
                    ? t('weeklySelection.tryAdjustingSearch')
                    : t('weeklySelection.noMealsAvailable')}
                </p>
              </div>
            )}

            {/* Meals grid */}
            {!isLoading && filteredMeals.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredMeals.map((meal) => (
                  <MealSelectionCard
                    key={meal.id}
                    meal={meal}
                    selectedCount={localSelections[meal.id] || 0}
                    onIncrement={() => handleIncrement(meal.id)}
                    onDecrement={() => handleDecrement(meal.id)}
                    disabled={bulkUpdateMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Summary sidebar */}
          <div className="lg:col-span-1">
            <WeeklySelectionSummary
              selectedCount={totalSelectedCount}
              requiredCount={requiredMealCount}
              selectedMeals={selectedMealsList}
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={bulkUpdateMutation.isPending}
              hasChanges={hasChanges}
            />
          </div>
        </div>
          </>
        )}

        {/* Family Tab */}
        {tab === 'family' && user.isOrganizer && (
          <>
            {/* Week Navigator */}
            <WeekNavigator
              currentWeekStart={weekStart}
              onPreviousWeek={goToPreviousWeek}
              onNextWeek={goToNextWeek}
              onToday={goToToday}
            />

            {/* Loading skeleton */}
            {familyLoading && <FamilySummarySkeleton />}

            {/* Family selections by member */}
            {!familyLoading && familyData?.members && (
              <div className="space-y-6">
                {familyData.members.map((member) => (
                  <FamilyMemberCard key={member.userId} member={member} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!familyLoading && familyData?.members.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <p className="text-sm text-gray-500">
                  {t('weeklySelection.noFamilyMembers')}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
